import { useRenderPassInfoText } from '@/components/Package/hooks/useRenderPassInfoText'
import { useAuthStore } from '@/lib/auth/AuthStore'
import { mixpanelEvent } from '@/lib/mixpanels/mixpanel'
import { numberFormatter } from '@/lib/utils'
import { SUBSCRIBE_PAYMENTS } from '@/settings/constant'
import { Button } from '@bookips/solvook-ui-library'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Col, Row } from 'antd'
import { useRouter } from 'next/router'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import usePassStore from 'src/store/pass'
import {
	DiscountRate,
	OriginalPrice,
	PassDialogButton,
	PassDialogButtonContainer,
	PassItemContainer,
	PassPrice,
	PassTitle,
	Price,
	PurchaseButton,
	SubDescription,
} from '.'
import { getMemberships, PassCategory, UseTicketProductInfo } from '../../api/membership'
import { getUserMeta } from '../../api/users'
import LoginDialog from '../../store/loginDialog'
import { SubscribeDialog } from './SubscribeToPass/SubscribeDialog'

export interface PassItemProps {
	pass: UseTicketProductInfo
	handleSubscribeWarningDialog: () => void
	existSolvook: () => void
}

//TODO : 남은과업 : 추상화, 관심사 분리
export const PassItem = ({ pass, handleSubscribeWarningDialog, existSolvook }: PassItemProps) => {
	const [isOpenSubscribeInfoModal, setIsOpenSubscribeInfoModal] = useState(false)

	const openPassInfoModal = usePassStore((state) => state.openPassInfoModal)
	const { setPurchasePass } = usePassStore((state) => ({
		setPurchasePass: state.setPurchasePass,
	}))

	const { isUnlimitedMaxPrice, isAvailableMaxPrice, isNoDiscount } = useRenderPassInfoText({
		pass,
	})

	const onOrder = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			e.stopPropagation()
			mixpanelEvent('Product Detail Button Clicked', {
				'Product Categories': pass.category,
				'Product ID': pass.id,
				'Product Title': pass.title,
				'Product Price': pass.price,
				'Button UI': 'Button',
			})
			//experts-subscribe와 solvook-subscribe의 구독 확인
			if (pass.category.includes('subscribe')) {
				setIsOpenSubscribeInfoModal(true)
				return
			}

			setPurchasePass(pass)
			openPassInfoModal({
				passInfo: pass,
			})
		},
		[pass],
	)
	const commonProps = {
		onOrder,
		isUnlimitedMaxPrice,
		isAvailableMaxPrice,
		availableMaxPrice: pass.availableMaxPrice,
		title: pass.title,
		price: pass.price,
		originPrice: pass.originPrice,
		discountRate: pass.discountRate,
		isNoDiscount,
		existSolvook,
	}

	useEffect(() => {
		if (isOpenSubscribeInfoModal) {
			mixpanelEvent('Product Detail Popup Viewed', {
				'Product Categories': pass.category,
				'Product ID': pass.id,
				'Product Title': pass.title,
				'Product Price': pass.price,
			})
		}
	}, [isOpenSubscribeInfoModal])

	return (
		<>
			{(() => {
				switch (pass.category) {
					case PassCategory.ExpertSubscription:
						return (
							<ExpertSubscriptionPassItem
								{...commonProps}
								pass={pass}
								setIsOpenSubscribeInfoModal={setIsOpenSubscribeInfoModal}
								isOpenSubscribeInfoModal={isOpenSubscribeInfoModal}
								handleSubscribeWarningDialog={handleSubscribeWarningDialog}
							/>
						)
					case PassCategory.Subscription:
						return (
							<SubscriptionPassItem
								{...commonProps}
								pass={pass}
								setIsOpenSubscribeInfoModal={setIsOpenSubscribeInfoModal}
								isOpenSubscribeInfoModal={isOpenSubscribeInfoModal}
								handleSubscribeWarningDialog={handleSubscribeWarningDialog}
							/>
						)
					case PassCategory.Expert:
						return <ExpertPassItem {...commonProps} />
					case PassCategory.Solvookplus:
						return <TicketPassItem {...commonProps} />
					case PassCategory.Ticket:
						return <TicketPassItem {...commonProps} />
					default:
						return <TicketPassItem {...commonProps} />
				}
			})()}
		</>
	)
}

interface CommonPassItemProps {
	onOrder: (e: unknown) => void
	isUnlimitedMaxPrice: boolean
	isAvailableMaxPrice: boolean
	availableMaxPrice: number
	title: string
	price: number
	originPrice: number
	discountRate: number
	isNoDiscount: boolean
	checkPass?: boolean
	existSolvook?: () => void
}

interface SubscribePassItemProps {
	pass: UseTicketProductInfo
	isOpenSubscribeInfoModal: boolean
	setIsOpenSubscribeInfoModal: (isOpen: boolean) => void
	handleSubscribeWarningDialog: () => void
}

const SubscriptionPassItem = ({
	onOrder,
	isUnlimitedMaxPrice,
	isAvailableMaxPrice,
	availableMaxPrice,
	title,
	price,
	originPrice,
	discountRate,
	isNoDiscount,
	pass,
	isOpenSubscribeInfoModal,
	setIsOpenSubscribeInfoModal,
	handleSubscribeWarningDialog,
}: CommonPassItemProps & SubscribePassItemProps) => {
	const { push } = useRouter()
	const { isLoggedIn } = useAuthStore()
	const { openLoginDialog } = LoginDialog()
	//구독여부 확인
	const userMetaMutate = useMutation(getUserMeta)

	const checkSubscription = useCallback(async () => {
		if (!isLoggedIn) {
			setIsOpenSubscribeInfoModal(false)
			openLoginDialog()
			return
		}
		try {
			const metaRes = await userMetaMutate.mutateAsync()
			if (
				metaRes &&
				metaRes?.solvookpluses.find(
					(item) => item.productInfo.id === pass.id && !item.expiredReason,
				)
			) {
				setIsOpenSubscribeInfoModal(false)
				handleSubscribeWarningDialog()
				return
			}
		} catch (e) {}
		setIsOpenSubscribeInfoModal(false)
		push(`${SUBSCRIBE_PAYMENTS}?id=${pass.id}`)
	}, [pass])

	return (
		<>
			<PassItemLayout
				title={title}
				description={
					<>
						{isUnlimitedMaxPrice && (
							<SubDescription>조건 내 무제한 사용</SubDescription>
						)}
						{isAvailableMaxPrice && (
							<SubDescription>
								{numberFormatter(availableMaxPrice)}원 이하 자료 교환가능
							</SubDescription>
						)}
					</>
				}
				priceInfo={
					<>
						{!isNoDiscount && (
							<OriginalPrice>월 {numberFormatter(originPrice)}원</OriginalPrice>
						)}
						<div className={'priceInfo'}>
							{!isNoDiscount && (
								<DiscountRate className={'ticket'}>{discountRate}%</DiscountRate>
							)}
							<Price>월 {numberFormatter(price)}원</Price>
						</div>
					</>
				}
				purchaseCTA={
					<Button variant="strokePrimary" onClick={(e) => onOrder(e)}>
						구독 시작하기
					</Button>
				}
				onClick={onOrder}
			/>
			<SubscribeDialog
				open={isOpenSubscribeInfoModal}
				onClose={() => setIsOpenSubscribeInfoModal(false)}
				contentInfo={pass}
				footer={
					<PassDialogButtonContainer>
						<PassDialogButton
							variant="strokeSecondary"
							onClick={() => {
								window.open(
									'https://solvookguide.oopy.io/78b924d5-140f-4475-bd9b-d13d99bedb2b',
									'_blank',
								)
							}}>
							이용방법 보기
						</PassDialogButton>
						<PassDialogButton variant="solid" onClick={() => checkSubscription()}>
							구독 시작하기
						</PassDialogButton>
					</PassDialogButtonContainer>
				}
			/>
		</>
	)
}

const TicketPassItem = ({
	onOrder,
	isUnlimitedMaxPrice,
	isAvailableMaxPrice,
	availableMaxPrice,
	title,
	price,
	originPrice,
	discountRate,
	isNoDiscount,
}: CommonPassItemProps) => {
	return (
		<>
			<PassItemLayout
				title={title}
				description={
					<>
						{isUnlimitedMaxPrice && (
							<SubDescription>조건 내 무제한 사용</SubDescription>
						)}
						{isAvailableMaxPrice && (
							<SubDescription>
								{numberFormatter(availableMaxPrice)}원 이하 자료 교환가능
							</SubDescription>
						)}
					</>
				}
				priceInfo={
					<>
						{!isNoDiscount && (
							<OriginalPrice>{numberFormatter(originPrice)}원</OriginalPrice>
						)}
						<div className={'priceInfo'}>
							{!isNoDiscount && (
								<DiscountRate className={'ticket'}>{discountRate}%</DiscountRate>
							)}
							<Price>{numberFormatter(price)}원</Price>
						</div>
					</>
				}
				purchaseCTA={<PurchaseButton onClick={onOrder}>구매하기</PurchaseButton>}
				onClick={onOrder}
			/>
		</>
	)
}
const ExpertSubscriptionPassItem = ({
	onOrder,
	title,
	price,
	originPrice,
	discountRate,
	isNoDiscount,
	pass,
	...props
}: CommonPassItemProps & SubscribePassItemProps) => {
	const { push } = useRouter()
	const { isLoggedIn } = useAuthStore()
	const { openLoginDialog } = LoginDialog()
	//구독여부 확인
	const { data: userMembershipResponse } = useQuery(
		['userMembership'],
		() => getMemberships({}),
		{
			onError: (error) => {
				console.log(error)
			},
			enabled: isLoggedIn,
		},
	)

	const checkSubscription = useCallback(async () => {
		if (!isLoggedIn) {
			props.setIsOpenSubscribeInfoModal(false)
			openLoginDialog()
			return
		}
		try {
			const isExistExpertSubscribe = userMembershipResponse?.unexpired.filter((item) => {
				if (item.productInfo.category.includes('expert-subscribe')) {
					return item
				}
			})

			const isExistSolvookPass = userMembershipResponse?.unexpired.filter((item) => {
				if (
					item.productInfo.category.includes('solvookplus') ||
					(item.productInfo.categoryTitle === '쏠북 플러스' &&
						item.productInfo.category.includes('ticket'))
				) {
					return item
				}
			})

			if (isExistSolvookPass.length) {
				props.setIsOpenSubscribeInfoModal(false)
				props.existSolvook()
				return
			}

			if (isExistExpertSubscribe.length) {
				props.setIsOpenSubscribeInfoModal(false)
				props.handleSubscribeWarningDialog()
				return
			}
		} catch (e) {
			console.error(e)
		}

		props.setIsOpenSubscribeInfoModal(false)
		push(`${SUBSCRIBE_PAYMENTS}?id=${pass.id}`)
	}, [pass, userMembershipResponse])

	return (
		<>
			<PassItemLayout
				title={title}
				description={<SubDescription>조건 내 시험지 생성 가능</SubDescription>}
				priceInfo={
					<>
						{!isNoDiscount && (
							<OriginalPrice>월 {numberFormatter(originPrice)}원</OriginalPrice>
						)}
						<div className={'priceInfo'}>
							{!isNoDiscount && (
								<DiscountRate className={'ticket'}>{discountRate}%</DiscountRate>
							)}
							<Price>월 {numberFormatter(price)}원</Price>
						</div>
					</>
				}
				purchaseCTA={
					<Button variant="strokePrimary" onClick={(e) => onOrder(e)}>
						구독 시작하기
					</Button>
				}
				onClick={(e) => onOrder(e)}
			/>
			<SubscribeDialog
				open={props.isOpenSubscribeInfoModal}
				onClose={() => {
					props.setIsOpenSubscribeInfoModal(false)
				}}
				contentInfo={pass}
				footer={
					<PassDialogButtonContainer>
						<PassDialogButton
							variant="strokeSecondary"
							onClick={() => {
								// mx-6
								mixpanelEvent('Guide Button Clicked', {
									'Guide Categories': 'Product Detail',
									'Product ID': pass.id,
									'Product Title': pass.title,
									'Guide URL': 'https://solvookguide.oopy.io/expert',
								})
								window.open(
									'https://solvookguide.oopy.io/expert',

									'_blank',
								)
							}}>
							이용방법 보기
						</PassDialogButton>
						<PassDialogButton
							variant="solid"
							onClick={() => {
								checkSubscription()
								//mx-3
								mixpanelEvent('Purchase Button Clicked', {
									'Product Categories': pass.category,
									'Product ID': pass.id,
									'Product Title': pass.title,
									'Product Price': pass.price,
								})
							}}>
							구독 시작하기
						</PassDialogButton>
					</PassDialogButtonContainer>
				}
			/>
		</>
	)
}
const ExpertPassItem = (props: CommonPassItemProps) => {
	return (
		<>
			<PassItemLayout
				title={props.title}
				description={<SubDescription>조건 내 시험지 생성 가능</SubDescription>}
				priceInfo={
					<>
						{!props.isNoDiscount && (
							<OriginalPrice>{numberFormatter(props.originPrice)}원</OriginalPrice>
						)}
						<div className={'priceInfo'}>
							{!props.isNoDiscount && (
								<DiscountRate className={'ticket'}>
									{props.discountRate}%
								</DiscountRate>
							)}
							<Price>{numberFormatter(props.price)}원</Price>
						</div>
					</>
				}
				purchaseCTA={
					<Button
						variant="strokePrimary"
						onClick={(e) => {
							props.onOrder(e)
						}}>
						구매하기
					</Button>
				}
				onClick={(e) => {
					props.onOrder(e)
				}}
			/>
		</>
	)
}

const PassItemLayout = ({
	priceInfo,
	onClick,
	purchaseCTA,
	description,
	title,
}: {
	priceInfo: ReactNode
	onClick: (e: unknown) => void
	purchaseCTA: ReactNode
	description?: ReactNode
	title: ReactNode
}) => {
	return (
		<PassItemContainer style={{ cursor: 'pointer' }} role="button" onClick={onClick}>
			<Row>
				<Col className={'clickArea'}>
					<PassTitle>{title}</PassTitle>
					{description}
				</Col>
				<Col className={'priceInfo'}>
					<PassPrice className={'left'}>{priceInfo}</PassPrice>
					<div className={'right'}>{purchaseCTA}</div>
				</Col>
			</Row>
		</PassItemContainer>
	)
}
